import pipelineAutomationsAPI from 'dashboard/api/pipelineAutomations';

const MAX_HISTORY = 50;

const defaultTriggerNode = () => ({
  id: 'trigger_1',
  type: 'trigger',
  position: { x: 300, y: 200 },
  data: {
    label: 'Item criado',
    type: 'item_created',
    event_type: 'pipeline_item_created',
  },
});

const cloneFlow = flow => JSON.parse(JSON.stringify(flow));

const state = {
  flow: { nodes: [], connections: [] },
  automations: [],
  currentAutomation: null,
  dragConnection: {
    active: false,
    nodeId: null,
    handleType: 'source',
    position: { x: 0, y: 0 },
  },
  selectedNodeId: null,
  history: [],
  historyIndex: -1,
  uiFlags: { isFetching: false, isSaving: false },
};

const getters = {
  getAutomations: s => s.automations,
  getCurrentAutomation: s => s.currentAutomation,
  getFlowNodes: s => s.flow.nodes,
  getFlowConnections: s => s.flow.connections,
  getSelectedNode: s =>
    s.flow.nodes.find(n => n.id === s.selectedNodeId) || null,
  getAutomationById: s => id =>
    s.automations.find(a => a.id === Number(id) || a.id === id) || null,
  canUndo: s => s.historyIndex > 0,
  canRedo: s => s.historyIndex < s.history.length - 1,
};

const mutations = {
  SET_UI_FLAG(s, flags) {
    s.uiFlags = { ...s.uiFlags, ...flags };
  },
  SET_AUTOMATIONS(s, automations) {
    s.automations = automations;
  },
  SET_CURRENT_AUTOMATION(s, automation) {
    s.currentAutomation = automation;
  },
  INIT_FLOW(s, flowData) {
    if (flowData && flowData.nodes && flowData.nodes.length > 0) {
      s.flow = {
        nodes: flowData.nodes,
        connections: flowData.connections || flowData.edges || [],
      };
    } else {
      s.flow = { nodes: [defaultTriggerNode()], connections: [] };
    }
    // Reset history
    s.history = [cloneFlow(s.flow)];
    s.historyIndex = 0;
    s.selectedNodeId = null;
  },
  setSelectedNode(s, nodeId) {
    s.selectedNodeId = nodeId;
  },
  addNode(s, node) {
    s.flow.nodes.push(node);
  },
  updateNode(s, { nodeId, updates, addToHistory = true }) {
    const index = s.flow.nodes.findIndex(n => n.id === nodeId);
    if (index === -1) return;
    const node = s.flow.nodes[index];
    s.flow.nodes[index] = {
      ...node,
      ...updates,
      position: updates.position ? updates.position : node.position,
      data: updates.data ? { ...node.data, ...updates.data } : node.data,
    };
    if (addToHistory) {
      mutations.recordCurrentState(s);
    }
  },
  removeNode(s, nodeId) {
    s.flow.nodes = s.flow.nodes.filter(n => n.id !== nodeId);
    s.flow.connections = s.flow.connections.filter(
      c => c.source !== nodeId && c.target !== nodeId
    );
    mutations.recordCurrentState(s);
  },
  addConnection(s, connection) {
    s.flow.connections.push(connection);
    mutations.recordCurrentState(s);
  },
  removeConnection(s, connectionId) {
    s.flow.connections = s.flow.connections.filter(c => c.id !== connectionId);
    mutations.recordCurrentState(s);
  },
  startConnectionDrag(s, { nodeId, handleType, position }) {
    s.dragConnection = { active: true, nodeId, handleType, position };
  },
  updateDragPosition(s, position) {
    s.dragConnection.position = position;
  },
  endConnectionDrag(s) {
    s.dragConnection = {
      active: false,
      nodeId: null,
      handleType: 'source',
      position: { x: 0, y: 0 },
    };
  },
  recordCurrentState(s) {
    // Truncate any redo history
    s.history = s.history.slice(0, s.historyIndex + 1);
    s.history.push(cloneFlow(s.flow));
    if (s.history.length > MAX_HISTORY) {
      s.history.shift();
    }
    s.historyIndex = s.history.length - 1;
  },
  undo(s) {
    if (s.historyIndex <= 0) return;
    s.historyIndex -= 1;
    s.flow = cloneFlow(s.history[s.historyIndex]);
  },
  redo(s) {
    if (s.historyIndex >= s.history.length - 1) return;
    s.historyIndex += 1;
    s.flow = cloneFlow(s.history[s.historyIndex]);
  },
};

const actions = {
  async fetchAutomations({ commit }) {
    commit('SET_UI_FLAG', { isFetching: true });
    try {
      const { data } = await pipelineAutomationsAPI.get();
      commit('SET_AUTOMATIONS', Array.isArray(data) ? data : []);
    } catch {
      commit('SET_AUTOMATIONS', []);
    } finally {
      commit('SET_UI_FLAG', { isFetching: false });
    }
  },

  async fetchAutomation({ commit }, id) {
    const { data } = await pipelineAutomationsAPI.show(id);
    commit('SET_CURRENT_AUTOMATION', data);
    return data;
  },

  async saveAutomation(
    { commit, state: s },
    { id, name, description, active, flow, trigger_type }
  ) {
    commit('SET_UI_FLAG', { isSaving: true });
    try {
      const payload = {
        name,
        description,
        active,
        flow,
        trigger_type: trigger_type || 'event',
      };
      let response;
      if (id) {
        response = await pipelineAutomationsAPI.update(id, payload);
      } else {
        response = await pipelineAutomationsAPI.create(payload);
      }
      const automation = response.data;
      // Refresh the automations list after save
      const current = s.automations;
      const idx = current.findIndex(a => a.id === automation.id);
      if (idx !== -1) {
        const updated = [...current];
        updated[idx] = automation;
        commit('SET_AUTOMATIONS', updated);
      } else {
        commit('SET_AUTOMATIONS', [...current, automation]);
      }
      return automation;
    } finally {
      commit('SET_UI_FLAG', { isSaving: false });
    }
  },

  async deleteAutomation({ commit, state: s }, id) {
    await pipelineAutomationsAPI.destroy(id);
    commit(
      'SET_AUTOMATIONS',
      s.automations.filter(a => a.id !== id)
    );
  },

  initFlow({ commit }, flowData = null) {
    commit('INIT_FLOW', flowData);
  },

  createNode({ commit }, { type, position, data }) {
    const id = `${type}_${Date.now()}`;
    const node = { id, type, position, data };
    commit('addNode', node);
    commit('recordCurrentState');
    return id;
  },

  connectNodes({ commit }, { sourceId, targetId, label = '' }) {
    const connection = {
      id: `conn_${sourceId}_${targetId}_${Date.now()}`,
      source: sourceId,
      target: targetId,
      label,
      animated: false,
    };
    commit('addConnection', connection);
    return connection;
  },

  updateNodeData({ commit }, { nodeId, data }) {
    commit('updateNode', { nodeId, updates: { data }, addToHistory: true });
  },

  async fetchExecutions(_, { automationId, params = {} }) {
    const { data } = await pipelineAutomationsAPI.executions(
      automationId,
      params
    );
    return data;
  },

  duplicateNode({ commit, state: s }, nodeId) {
    const node = s.flow.nodes.find(n => n.id === nodeId);
    if (!node) return undefined;
    const newNode = {
      ...JSON.parse(JSON.stringify(node)),
      id: `${node.type}_${Date.now()}`,
      position: { x: node.position.x + 30, y: node.position.y + 30 },
    };
    commit('addNode', newNode);
    commit('recordCurrentState');
    return newNode.id;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
